import { SITE, createWhatsAppLink } from "../config/site";
import { InstagramIcon, TikTokIcon } from "../components/SocialIcons";

const CONTACT_CARDS = [
  {
    title: "Call the shop",
    value: SITE.phoneDisplay,
    href: SITE.phoneHref,
    helper: "Reach us directly for orders, stock checks, or delivery support.",
  },
  {
    title: "Email us",
    value: SITE.email,
    href: `mailto:${SITE.email}`,
    helper: "Best for formal inquiries, supplier questions, and follow-up details.",
  },
  {
    title: "Visit us",
    value: `${SITE.locationTitle}\n${SITE.locationLine}`,
    helper: "Find us at OTC Wholesale Mall for in-store shopping and pickup.",
  },
  {
    title: "Message on WhatsApp",
    value: "Fast order help",
    href: createWhatsAppLink(),
    helper: "Send your order details, preferred style, and any questions.",
  },
];

export default function Contact() {
  return (
    <main className="mx-auto max-w-7xl px-4 py-10 sm:px-6 lg:px-8">
      <section className="overflow-hidden rounded-[2rem] border border-orange-100 bg-[radial-gradient(circle_at_top_left,_#fff7f3,_#ffffff_55%,_#fff4ee)] px-6 py-8 shadow-sm sm:px-8 sm:py-10">
        <p className="text-xs font-semibold uppercase tracking-[0.25em] text-[#e46a2e]">Contact Petrah Collection</p>
        <h1 className="mt-3 text-4xl font-black tracking-tight text-slate-950 sm:text-5xl">
          All the store details, clearly in one place.
        </h1>
        <p className="mt-4 max-w-3xl text-base leading-7 text-slate-600">
          Whether you want to visit the showroom, confirm an order, or ask about a style, you can reach Petrah Collection through phone, email, WhatsApp, Instagram, or TikTok.
        </p>
      </section>

      <section className="mt-8 grid gap-4 md:grid-cols-2">
        {CONTACT_CARDS.map((card) => (
          <InfoCard key={card.title} {...card} />
        ))}
      </section>

      <section className="mt-8 grid gap-4 lg:grid-cols-[1.05fr_0.95fr]">
        <div className="rounded-[2rem] border border-slate-200 bg-white p-6 shadow-sm">
          <p className="text-xs font-semibold uppercase tracking-[0.25em] text-[#e46a2e]">Shop location</p>
          <h2 className="mt-2 text-2xl font-black tracking-tight text-slate-950">Visit our OTC Wholesale Mall shop</h2>
          <div className="mt-5 grid gap-4 sm:grid-cols-2">
            <DetailBlock label="Location" value={SITE.locationTitle} />
            <DetailBlock label="Floor / unit" value={SITE.locationLine} />
            <DetailBlock label="Business hours" value={SITE.businessHoursDetailed} />
            <DetailBlock label="Support" value="Order help, in-store assistance, and pickup coordination." />
          </div>
        </div>

        <div className="rounded-[2rem] border border-orange-100 bg-[#fff7f3] p-6 shadow-sm">
          <p className="text-xs font-semibold uppercase tracking-[0.25em] text-[#e46a2e]">Social media</p>
          <h2 className="mt-2 text-2xl font-black tracking-tight text-slate-950">Follow the brand online</h2>
          <p className="mt-3 text-sm leading-6 text-slate-600">
            Stay updated on fresh arrivals, styling inspiration, and the latest looks from Petrah Collection.
          </p>

          <div className="mt-5 space-y-3">
            <SocialLink
              title="Instagram"
              handle="@petrahcollection"
              href={SITE.instagram}
              helper="Browse brand content, updates, and new product highlights."
              icon={<InstagramIcon className="h-8 w-8" />}
              iconClassName="bg-[linear-gradient(135deg,#fd5949,#d6249f,#285AEB)] text-white"
            />
            <SocialLink
              title="TikTok"
              handle="@petrah0372"
              href={SITE.tiktok}
              helper="Watch quick product clips, trending styles, and more."
              icon={<TikTokIcon className="h-8 w-8" />}
              iconClassName="bg-slate-950 text-white"
            />
          </div>
        </div>
      </section>
    </main>
  );
}

function InfoCard({ title, value, helper, href }) {
  const inner = (
    <>
      <p className="text-xs font-semibold uppercase tracking-[0.25em] text-slate-500">{title}</p>
      <div className="mt-3 whitespace-pre-line text-xl font-black tracking-tight text-slate-950">{value}</div>
      <p className="mt-3 text-sm leading-6 text-slate-600">{helper}</p>
    </>
  );

  if (href) {
    return (
      <a
        href={href}
        target={href.startsWith("http") ? "_blank" : undefined}
        rel={href.startsWith("http") ? "noreferrer" : undefined}
        className="rounded-[2rem] border border-slate-200 bg-white p-6 shadow-sm transition hover:-translate-y-0.5 hover:border-[#e46a2e]"
      >
        {inner}
      </a>
    );
  }

  return <div className="rounded-[2rem] border border-slate-200 bg-white p-6 shadow-sm">{inner}</div>;
}

function DetailBlock({ label, value }) {
  return (
    <div className="rounded-[1.5rem] border border-slate-200 bg-slate-50 p-4">
      <div className="text-xs font-semibold uppercase tracking-[0.2em] text-slate-500">{label}</div>
      <div className="mt-2 whitespace-pre-line text-sm font-semibold leading-6 text-slate-900">{value}</div>
    </div>
  );
}

function SocialLink({ title, handle, helper, href, icon, iconClassName }) {
  return (
    <a
      href={href}
      target="_blank"
      rel="noreferrer"
      className="flex items-center gap-4 rounded-[1.5rem] border border-orange-100 bg-white p-4 transition hover:border-[#e46a2e]"
    >
      <div className={`flex h-16 w-16 shrink-0 items-center justify-center rounded-[1.25rem] shadow-sm ${iconClassName}`}>
        {icon}
      </div>
      <div>
        <div className="text-lg font-bold tracking-tight text-slate-950">{title}</div>
        <div className="mt-1 text-sm font-semibold text-[#e46a2e]">{handle}</div>
        <p className="mt-2 text-sm leading-6 text-slate-600">{helper}</p>
      </div>
    </a>
  );
}